import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { listUsers } from '../api/admin';
import { useInfiniteList } from '../api/useInfiniteList';
import { Avatar, relativeTime } from '../components/ui';

const PAGE_SIZE = 30;
const SEARCH_DEBOUNCE_MS = 300;

/**
 * List of real players, 30 per page with infinite scroll.
 *
 * Clicking a row opens that player's read-only detail page (stats, recent
 * matches, cards & decks). Mutation still applies to bots only, and the server
 * enforces that independently (every bot route rejects a non-bot id) rather than
 * relying on the UI to hide the affordance.
 */
export function UsersPage(): React.JSX.Element {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(query.trim()), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [query]);

  // Identity changes when the query does, which resets the list — otherwise
  // filtered rows would be appended to unfiltered ones.
  const fetchPage = useCallback(
    (cursor: string | null) => listUsers(cursor, debounced, PAGE_SIZE),
    [debounced],
  );

  const { items, loading, error, hasMore, sentinelRef } = useInfiniteList(fetchPage);

  return (
    <div className="page">
      <header className="page__header">
        <div>
          <h1>Usuários</h1>
          <p className="page__subtitle">
            {items.length} carregado(s){hasMore ? '…' : ''} · somente leitura
          </p>
        </div>
        <input
          className="search"
          placeholder="Buscar por nome ou usuário…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </header>

      {error ? <div className="banner banner--error">{error.message}</div> : null}

      <div className="data-table" role="table">
        <div className="data-table__head" role="row">
          <span role="columnheader">Jogador</span>
          <span role="columnheader">Usuário</span>
          <span role="columnheader">Último acesso</span>
          <span role="columnheader">Entrou em</span>
        </div>

        {items.map((user) => (
          <div
            className="data-table__row data-table__row--clickable"
            role="row"
            key={user.id}
            onClick={() => navigate(`/users/${user.id}`)}
          >
            <span className="cell cell--identity" role="cell">
              <Avatar src={user.profileImgUrl} name={user.displayName} />
              <span className="cell__name">{user.displayName}</span>
            </span>
            <span className="cell cell--dim" role="cell">
              @{user.userName}
            </span>
            <span className="cell" role="cell" title={user.lastSeenAt ?? ''}>
              {relativeTime(user.lastSeenAt)}
            </span>
            <span className="cell cell--dim" role="cell">
              {new Date(user.createdAt).toLocaleDateString('pt-BR')}
            </span>
          </div>
        ))}

        {!loading && items.length === 0 ? (
          <div className="empty-state">
            {debounced ? 'Nenhum usuário encontrado.' : 'Nenhum usuário ainda.'}
          </div>
        ) : null}
      </div>

      {loading ? <div className="page-loading">Carregando…</div> : null}
      {/* Sentinel: entering the viewport triggers the next page. */}
      {hasMore ? <div ref={sentinelRef} className="scroll-sentinel" /> : null}
      {!hasMore && items.length > 0 ? <div className="list-end">Fim da lista.</div> : null}
    </div>
  );
}
